import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ScreenRotationOutlinedIcon from "@mui/icons-material/ScreenRotationOutlined";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  fetchNominasResumen,
  saveNominasAjuste,
} from "../../api/Nominas/NominasApi";
import NominasCosteTrabajadorChart from "./NominasCosteTrabajadorChart";
import NominasTarjetas from "./NominasTarjetas";
import {
  NOMINAS_AJUSTES,
  calcularDescuentosTrabajador,
  descuentosPorAjusteDesdeServidor,
} from "./nominasAjustes";

const AJUSTE_LYDIA = NOMINAS_AJUSTES[0];

const MESES_LABEL = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

const EMPRESA_OPTIONS = [
  { value: "stage", label: "Stage Design" },
  { value: "erna", label: "ERNA" },
  { value: "fusion", label: "Fusión (Stage + ERNA)" },
];

const formatearImporte = (valor) =>
  (valor ?? 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

function estiloLineaExtraNomina(ln, theme) {
  if (ln.esRevolut) {
    return {
      color: theme.palette.secondary.main,
      tag: "(tarjeta)",
      destacada: true,
    };
  }
  if (ln.esFormacion) {
    return {
      color: theme.palette.info.main,
      tag: "(629 / UOC)",
      destacada: true,
    };
  }
  if (ln.esVehiculo) {
    return {
      color: theme.palette.warning.main,
      tag: "(629)",
      destacada: true,
    };
  }
  if (ln.esTren) {
    return {
      color: theme.palette.success.main,
      tag: "(629 Renfe)",
      destacada: true,
    };
  }
  return { color: "text.secondary", tag: null, destacada: false };
}

const Nominas = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { contentMargin, isMobile } = useContext(SidebarContext);
  const isLandscape = useMediaQuery("(orientation: landscape)", {
    noSsr: true,
    defaultMatches: false,
  });
  const showRotatePrompt = isMobile && !isLandscape;
  const mobileLandscape = isMobile && isLandscape;
  const isDark = theme.palette.mode === "dark";

  const [year, setYear] = useState(new Date().getFullYear());
  const [empresa, setEmpresa] = useState("stage");
  const [vista, setVista] = useState("trabajador");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGrupos, setExpandedGrupos] = useState({
    sueldos: true,
    cargas: true,
  });
  const [expandedTrabajadores, setExpandedTrabajadores] = useState({});
  const [descuentoComisionesLydia, setDescuentoComisionesLydia] = useState(0);
  const [ajustesServidor, setAjustesServidor] = useState([]);
  const [guardandoAjuste, setGuardandoAjuste] = useState(false);
  const saveAjusteTimerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNominasResumen({ year, empresa });
      setData(res);
      setAjustesServidor(res?.ajustes || []);
      const lydiaAjuste = (res?.ajustes || []).find(
        (a) => a.id === AJUSTE_LYDIA.id,
      );
      setDescuentoComisionesLydia(Number(lydiaAjuste?.descuentoMensual) || 0);
      setExpandedGrupos({ sueldos: true, cargas: true });
      setExpandedTrabajadores({});
    } catch (e) {
      setData(null);
      setError(
        e.response?.data?.error ||
          e.message ||
          "No se pudieron cargar las nóminas",
      );
    } finally {
      setLoading(false);
    }
  }, [year, empresa]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const grupos = data?.grupos || [];
  const trabajadores = data?.trabajadores || [];
  const total = data?.total;
  const otrosGrupo = data?.otrosGrupo;
  const tesoreria = data?.tesoreria;
  const reconciliacion = data?.meta?.reconciliacion;
  const revolutBorja = data?.revolutBorja;
  const formacionBorja = data?.formacionBorja;
  const vehiculoLydia = data?.vehiculoLydia;
  const cocheMikael = data?.cocheMikael;
  const trenGuillermo = data?.trenGuillermo;

  const totalesPorMes = useMemo(() => {
    const out = {};
    if (!total?.meses) return out;
    for (let m = 1; m <= 12; m++) {
      out[m] = total.meses[String(m)] || 0;
    }
    return out;
  }, [total]);

  const empresaLabel =
    EMPRESA_OPTIONS.find((o) => o.value === empresa)?.label || empresa;

  const toggleGrupo = (id) => {
    setExpandedGrupos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleTrabajador = (id) => {
    setExpandedTrabajadores((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const stickyCell = {
    position: "sticky",
    left: 0,
    zIndex: 2,
    bgcolor: theme.palette.background.paper,
  };

  const renderTablaHead = () => (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{
            ...stickyCell,
            zIndex: 3,
            fontWeight: 800,
            minWidth: 280,
          }}
        >
          {vista === "trabajador" ? "Trabajador / concepto" : "Concepto (A3)"}
        </TableCell>
        {MESES_LABEL.map((label) => (
          <TableCell
            key={label}
            align="right"
            sx={{ fontWeight: 700, whiteSpace: "nowrap", minWidth: 88 }}
          >
            {label}
          </TableCell>
        ))}
        <TableCell
          align="right"
          sx={{
            fontWeight: 800,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderLeft: `2px solid ${theme.palette.divider}`,
            minWidth: 110,
          }}
        >
          Total
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderFilaTotal = () =>
    total ? (
      <TableRow
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          "& .MuiTableCell-root": { fontWeight: 800 },
        }}
      >
        <TableCell
          sx={{
            ...stickyCell,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        >
          Total coste por trabajador (A3 + extras)
        </TableCell>
        {MESES_LABEL.map((_, idx) => {
          const mes = idx + 1;
          const v = totalesPorMes[mes] || 0;
          return (
            <TableCell key={mes} align="right">
              {v ? formatearImporte(v) : "—"}
            </TableCell>
          );
        })}
        <TableCell
          align="right"
          sx={{
            borderLeft: `2px solid ${theme.palette.divider}`,
            color: theme.palette.primary.dark,
          }}
        >
          {formatearImporte(total.total)}
        </TableCell>
      </TableRow>
    ) : null;

  const ajustesVigentes = useMemo(() => {
    const base = [...ajustesServidor];
    const idx = base.findIndex((a) => a.id === AJUSTE_LYDIA.id);
    const lydia = {
      ...(idx >= 0 ? base[idx] : { id: AJUSTE_LYDIA.id }),
      descuentoMensual: descuentoComisionesLydia,
      activo: descuentoComisionesLydia > 0,
    };
    if (idx >= 0) base[idx] = lydia;
    else base.push(lydia);
    return base;
  }, [ajustesServidor, descuentoComisionesLydia]);

  const descuentosPorAjusteTabla = useMemo(
    () => descuentosPorAjusteDesdeServidor(ajustesVigentes),
    [ajustesVigentes],
  );

  const persistirAjusteLydia = useCallback(
    async (valor) => {
      const v = Math.max(0, Number(valor) || 0);
      setGuardandoAjuste(true);
      try {
        const saved = await saveNominasAjuste({
          year,
          empresa,
          ajusteId: AJUSTE_LYDIA.id,
          descuentoMensual: v,
          activo: v > 0,
        });
        setAjustesServidor((prev) => {
          const next = prev.filter((a) => a.id !== saved.id);
          return [...next, saved];
        });
      } catch (e) {
        setError(
          e.response?.data?.error ||
            e.message ||
            "No se pudo guardar el ajuste de nóminas",
        );
      } finally {
        setGuardandoAjuste(false);
      }
    },
    [year, empresa],
  );

  const handleDescuentoComisionesLydia = useCallback(
    (valor) => {
      const v = Math.max(0, Number(valor) || 0);
      setDescuentoComisionesLydia(v);
      if (saveAjusteTimerRef.current) clearTimeout(saveAjusteTimerRef.current);
      saveAjusteTimerRef.current = setTimeout(() => {
        persistirAjusteLydia(v);
      }, 700);
    },
    [persistirAjusteLydia],
  );

  useEffect(
    () => () => {
      if (saveAjusteTimerRef.current) clearTimeout(saveAjusteTimerRef.current);
    },
    [],
  );

  const renderVistaTrabajador = () => (
    <>
      {trabajadores.map((trab) => {
        const open = !!expandedTrabajadores[trab.id];
        const { descuentoTotal, descuentoPorMes } =
          calcularDescuentosTrabajador(
            trab,
            NOMINAS_AJUSTES,
            descuentosPorAjusteTabla,
          );
        const totalAjustado =
          descuentoTotal > 0
            ? Math.max(0, (trab.total || 0) - descuentoTotal)
            : null;
        return (
          <React.Fragment key={trab.id}>
            <TableRow
              hover
              sx={{ "& > *": { borderBottom: open ? 0 : undefined } }}
            >
              <TableCell
                sx={{ ...stickyCell, fontWeight: 700, fontSize: "0.85rem" }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => toggleTrabajador(trab.id)}
                  >
                    {open ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                  <span>{trab.label}</span>
                </Stack>
              </TableCell>
              {MESES_LABEL.map((_, idx) => {
                const mes = idx + 1;
                const v = trab.meses[String(mes)] || 0;
                return (
                  <TableCell
                    key={mes}
                    align="right"
                    sx={{ fontWeight: 700, fontSize: "0.8rem" }}
                  >
                    {v ? formatearImporte(v) : "—"}
                  </TableCell>
                );
              })}
              <TableCell
                align="right"
                sx={{
                  fontWeight: 800,
                  borderLeft: `2px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                }}
              >
                {formatearImporte(trab.total)}
                {totalAjustado != null ? (
                  <Typography
                    component="div"
                    variant="caption"
                    sx={{
                      color: "red",
                      fontWeight: 800,
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    Ajustado: {formatearImporte(totalAjustado)}
                  </Typography>
                ) : null}
              </TableCell>
            </TableRow>
            {trab.lineas?.map((ln) => {
              const extra = estiloLineaExtraNomina(ln, theme);
              const celdaExtra = extra.destacada
                ? { fontWeight: 700, color: extra.color }
                : {};
              return (
              <TableRow
                key={ln.id}
                sx={{
                  display: open ? "table-row" : "none",
                  bgcolor: alpha(theme.palette.action.hover, 0.04),
                  "& .MuiTableCell-root": {
                    borderBottom: open ? undefined : 0,
                    py: 0.5,
                  },
                }}
              >
                <TableCell
                  sx={{
                    ...stickyCell,
                    minWidth: 280,
                    pl: 6.5,
                    fontSize: "0.75rem",
                    color: extra.color,
                    fontWeight: extra.destacada ? 700 : 400,
                  }}
                >
                  {ln.label}
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, fontFamily: "monospace" }}
                  >
                    {ln.cuenta}
                  </Typography>
                  {extra.tag ? (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, fontWeight: 700 }}
                    >
                      {extra.tag}
                    </Typography>
                  ) : null}
                </TableCell>
                {MESES_LABEL.map((_, idx) => {
                  const mes = idx + 1;
                  const v = ln.meses[String(mes)] || 0;
                  return (
                    <TableCell
                      key={mes}
                      align="right"
                      sx={{
                        fontSize: "0.75rem",
                        minWidth: 88,
                        ...celdaExtra,
                      }}
                    >
                      {v ? formatearImporte(v) : "—"}
                    </TableCell>
                  );
                })}
                <TableCell
                  align="right"
                  sx={{
                    fontSize: "0.75rem",
                    minWidth: 110,
                    borderLeft: `2px solid ${theme.palette.divider}`,
                    ...(extra.destacada
                      ? { fontWeight: 800, color: extra.color }
                      : {}),
                  }}
                >
                  {formatearImporte(ln.total)}
                </TableCell>
              </TableRow>
            );
            })}
            {open && descuentoTotal > 0 ? (
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.06),
                  "& .MuiTableCell-root": { py: 0.5 },
                }}
              >
                <TableCell
                  sx={{
                    ...stickyCell,
                    minWidth: 280,
                    pl: 6.5,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: theme.palette.error.main,
                  }}
                >
                  {AJUSTE_LYDIA.label}
                  {/* <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, opacity: 0.85 }}
                  >
                    ajuste visual
                  </Typography> */}
                </TableCell>
                {MESES_LABEL.map((_, idx) => {
                  const mes = idx + 1;
                  const d = descuentoPorMes[String(mes)] || 0;
                  return (
                    <TableCell
                      key={mes}
                      align="right"
                      sx={{
                        fontSize: "0.75rem",
                        minWidth: 88,
                        fontWeight: 700,
                        color: theme.palette.error.main,
                      }}
                    >
                      {d ? formatearImporte(-d) : "—"}
                    </TableCell>
                  );
                })}
                <TableCell
                  align="right"
                  sx={{
                    fontSize: "0.75rem",
                    minWidth: 110,
                    fontWeight: 800,
                    color: theme.palette.error.main,
                    borderLeft: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  {formatearImporte(-descuentoTotal)}
                </TableCell>
              </TableRow>
            ) : null}
          </React.Fragment>
        );
      })}
      {otrosGrupo?.filas?.length ? (
        <>
          <TableRow sx={{ bgcolor: alpha(theme.palette.warning.main, 0.08) }}>
            <TableCell
              colSpan={14}
              sx={{ fontWeight: 700, fontSize: "0.8rem" }}
            >
              {otrosGrupo.label}
            </TableCell>
          </TableRow>
          {otrosGrupo.filas.map((fila) => (
            <TableRow key={fila.id} hover>
              <TableCell sx={{ ...stickyCell, pl: 3, fontSize: "0.8rem" }}>
                {fila.label}
              </TableCell>
              {MESES_LABEL.map((_, idx) => {
                const mes = idx + 1;
                const v = fila.meses[String(mes)] || 0;
                return (
                  <TableCell
                    key={mes}
                    align="right"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {v ? formatearImporte(v) : "—"}
                  </TableCell>
                );
              })}
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  borderLeft: `2px solid ${theme.palette.divider}`,
                }}
              >
                {formatearImporte(fila.total)}
              </TableCell>
            </TableRow>
          ))}
        </>
      ) : null}
    </>
  );

  const renderVistaDetalle = () => (
    <>
      {grupos.map((grupo) => {
        const open = expandedGrupos[grupo.id] !== false;
        return (
          <React.Fragment key={grupo.id}>
            <TableRow
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "& .MuiTableCell-root": { fontWeight: 800 },
              }}
            >
              <TableCell
                sx={{
                  ...stickyCell,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => toggleGrupo(grupo.id)}
                  >
                    {open ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Typography variant="body2" fontWeight={800}>
                    {grupo.label}
                  </Typography>
                </Stack>
              </TableCell>
              {MESES_LABEL.map((_, idx) => {
                const mes = idx + 1;
                const v = grupo.subtotal?.meses[String(mes)] || 0;
                return (
                  <TableCell key={mes} align="right">
                    {v ? formatearImporte(v) : "—"}
                  </TableCell>
                );
              })}
              <TableCell
                align="right"
                sx={{ borderLeft: `2px solid ${theme.palette.divider}` }}
              >
                {formatearImporte(grupo.subtotal?.total)}
              </TableCell>
            </TableRow>
            {open
              ? grupo.filas.map((fila) => (
                  <TableRow key={fila.id} hover>
                    <TableCell
                      sx={{
                        ...stickyCell,
                        pl: 5,
                        fontSize: "0.8rem",
                        fontWeight: 500,
                      }}
                    >
                      {fila.label}
                      <Typography
                        component="div"
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {fila.cuenta}
                      </Typography>
                    </TableCell>
                    {MESES_LABEL.map((_, idx) => {
                      const mes = idx + 1;
                      const v = fila.meses[String(mes)] || 0;
                      return (
                        <TableCell
                          key={mes}
                          align="right"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {v ? formatearImporte(v) : "—"}
                        </TableCell>
                      );
                    })}
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        borderLeft: `2px solid ${theme.palette.divider}`,
                      }}
                    >
                      {formatearImporte(fila.total)}
                    </TableCell>
                  </TableRow>
                ))
              : null}
          </React.Fragment>
        );
      })}
    </>
  );

  if (showRotatePrompt) {
    return (
      <Box
        sx={{
          ...contentMargin,
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            maxWidth: 320,
            textAlign: "center",
            p: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, isDark ? 0.35 : 0.08)}`,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 2,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              animation: "nominasRotateHint 2.4s ease-in-out infinite",
              "@keyframes nominasRotateHint": {
                "0%, 100%": { transform: "rotate(0deg)" },
                "35%, 65%": { transform: "rotate(90deg)" },
              },
            }}
          >
            <ScreenRotationOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Gira el dispositivo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nóminas está optimizada para móvil en horizontal. Rota el teléfono
            para ver la matriz mensual.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        ...contentMargin,
        ...(mobileLandscape
          ? {
              width: "100%",
              height: "100dvh",
              maxHeight: "100dvh",
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              p: 0.75,
              pb: 10,
            }
          : {
              p: { xs: 1.25, sm: 2 },
            }),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          p: mobileLandscape ? 1 : { xs: 1.25, sm: 1.75 },
          mb: mobileLandscape ? 0.75 : 1.5,
          borderRadius: mobileLandscape ? 2.5 : 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.85),
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`
              : "linear-gradient(135deg, #eef4ff 0%, #f8f5ff 55%, #f4faf6 100%)",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 8px 24px rgba(0,0,0,0.35)"
              : "0 8px 24px rgba(15,23,42,0.08)",
        }}
      >
        <Stack
          direction={mobileLandscape ? "row" : { xs: "column", md: "row" }}
          spacing={mobileLandscape ? 1 : 1.5}
          justifyContent="space-between"
          alignItems={
            mobileLandscape ? "center" : { xs: "flex-start", md: "center" }
          }
        >
          <Box sx={{ minWidth: 0 }}>
            {!mobileLandscape ? (
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 1.1 }}
              >
                FINANCE · CONTABILIDAD
              </Typography>
            ) : null}
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                lineHeight: 1.1,
                fontSize: mobileLandscape ? "1rem" : undefined,
                whiteSpace: mobileLandscape ? "nowrap" : undefined,
              }}
            >
              {t("sidebar.nominas")}
            </Typography>
            {mobileLandscape ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {year} · {empresaLabel} · desplázate para ver todo
              </Typography>
            ) : (
              <Stack
                direction="row"
                spacing={0.7}
                sx={{ mt: 0.75 }}
                useFlexGap
                flexWrap="wrap"
              >
                <Chip
                  size="small"
                  icon={<PaymentsOutlinedIcon sx={{ fontSize: 16 }} />}
                  label={`Año ${year}`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={empresaLabel}
                  sx={{ fontWeight: 700 }}
                />
                {total ? (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`Coste A3 + extras: ${formatearImporte(total.total)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {reconciliacion?.totalPyGEp6 ? (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={`PyG analítico ep.6: ${formatearImporte(reconciliacion.totalPyGEp6)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {revolutBorja?.total ? (
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label={`Revolut Borja: ${formatearImporte(revolutBorja.total)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {formacionBorja?.total ? (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={`Formación Borja: ${formatearImporte(formacionBorja.total)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {vehiculoLydia?.total ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Vehículo Lydia: ${formatearImporte(vehiculoLydia.total)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {cocheMikael?.total ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Coche Mikael: ${formatearImporte(cocheMikael.total)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {trenGuillermo?.total ? (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`Tren Guillermo: ${formatearImporte(trenGuillermo.total)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
                {tesoreria?.total ? (
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label={`Desembolso anual: ${formatearImporte(tesoreria.total.total)}`}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            alignItems="center"
            sx={{
              flexShrink: 0,
              flexWrap: mobileLandscape ? "nowrap" : "wrap",
            }}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={vista}
              onChange={(_, v) => v && setVista(v)}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                borderRadius: 1.5,
              }}
            >
              <ToggleButton
                value="trabajador"
                sx={{
                  px: mobileLandscape ? 1 : undefined,
                  fontSize: mobileLandscape ? "0.7rem" : undefined,
                }}
              >
                {mobileLandscape ? "Trabajador" : "Por trabajador"}
              </ToggleButton>
              <ToggleButton
                value="detalle"
                sx={{
                  px: mobileLandscape ? 1 : undefined,
                  fontSize: mobileLandscape ? "0.7rem" : undefined,
                }}
              >
                {mobileLandscape ? "A3" : "Como A3"}
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControl
              size="small"
              sx={{ minWidth: mobileLandscape ? 76 : { xs: "100%", sm: 120 } }}
            >
              <InputLabel>Año</InputLabel>
              <Select
                value={year}
                label="Año"
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{
                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                  borderRadius: 1.5,
                }}
              >
                {[2023, 2024, 2025, 2026].map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: mobileLandscape ? 100 : { xs: "100%", sm: 200 } }}
            >
              <InputLabel>Empresa</InputLabel>
              <Select
                value={empresa}
                label="Empresa"
                onChange={(e) => setEmpresa(String(e.target.value))}
                sx={{
                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                  borderRadius: 1.5,
                }}
              >
                {EMPRESA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {mobileLandscape && opt.value === "fusion"
                      ? "Fusión"
                      : opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Box>

      <Alert
        severity="info"
        sx={{
          mb: 1.5,
          flexShrink: 0,
          ...(mobileLandscape
            ? { py: 0.75, "& .MuiAlert-message": { fontSize: "0.75rem" } }
            : {}),
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            mb: mobileLandscape ? 0.5 : 1,
            fontSize: mobileLandscape ? "0.75rem" : undefined,
          }}
        >
          <strong>Por trabajador</strong>: salario (640) + SS empresa (642) tal
          como en <strong>A3 / Excel RRHH</strong>, más{" "}
          <strong>comisiones, bonus y dietas</strong> si existen.{" "}
          <strong>Lydia Uralde</strong>: el importe en rojo resta{" "}
          <strong>1.043 €/mes</strong> de atrasos en comisiones (64000001).
        </Typography>
        {reconciliacion?.totalPyGEp6 ? (
          <Typography
            variant="body2"
            component="div"
            sx={{
              mb: mobileLandscape ? 0.5 : 1,
              fontSize: mobileLandscape ? "0.75rem" : undefined,
            }}
            color="text.secondary"
          >
            Referencia PyG analítico ep.6:{" "}
            {formatearImporte(reconciliacion.totalPyGEp6)}
            {reconciliacion.deltaPyG != null
              ? ` (Δ tabla A3: ${formatearImporte(reconciliacion.deltaPyG)})`
              : ""}
            . La diferencia viene de abonos contables y del cuadre de la 642 en
            el PyG, no de esta tabla.
          </Typography>
        ) : null}
        <Typography
          variant="body2"
          component="div"
          sx={{ fontSize: mobileLandscape ? "0.75rem" : undefined }}
        >
          <strong>
            IRPF (475) y SS del trabajador (476) no faltan como “gasto extra”
          </strong>
          : son retenciones del bruto (van a Hacienda y a la Seguridad Social).
          No se suman otra vez encima del 640. Abajo verás el{" "}
          <strong>desembolso en banco</strong> (465 + 476 + 475).
        </Typography>
      </Alert>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, py: 2, mb: 1 }}
        >
          <CircularProgress size={24} />
          <Typography color="text.secondary">Cargando nóminas…</Typography>
        </Box>
      ) : null}

      {!loading ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: mobileLandscape ? 2.5 : 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.8),
            ...(mobileLandscape
              ? {
                  maxHeight: "52vh",
                  minHeight: 220,
                  overflow: "auto",
                  WebkitOverflowScrolling: "touch",
                  mb: 1.5,
                }
              : {
                  maxHeight: "90vh",
                  overflow: "auto",
                  mb: 2,
                }),
          }}
        >
          <Table size="small" stickyHeader={!mobileLandscape}>
            {renderTablaHead()}
            <TableBody>
              {vista === "trabajador"
                ? renderVistaTrabajador()
                : renderVistaDetalle()}
              {renderFilaTotal()}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: mobileLandscape ? 1.25 : 2,
          mb: 2,
        }}
      >
        {!loading ? (
          <NominasCosteTrabajadorChart
            embedded
            trabajadores={trabajadores}
            otrosGrupo={otrosGrupo}
            year={year}
            formatearImporte={formatearImporte}
            descuentoComisionesLydia={descuentoComisionesLydia}
            onDescuentoComisionesLydiaChange={handleDescuentoComisionesLydia}
            guardandoAjuste={guardandoAjuste}
          />
        ) : null}
        <NominasTarjetas
          embedded
          year={year}
          empresa={empresa}
          formatearImporte={formatearImporte}
        />
      </Box>

      {!loading && tesoreria?.filas?.length ? (
        <Paper
          sx={{
            mt: mobileLandscape ? 0 : 2,
            p: mobileLandscape ? 1.25 : 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.8),
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={800}
            gutterBottom
            sx={{ fontSize: mobileLandscape ? "0.95rem" : undefined }}
          >
            Desembolso / tesorería (sin desglose por trabajador)
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, fontSize: mobileLandscape ? "0.75rem" : undefined }}
          >
            Movimientos en <strong>V_Asientos_Nominas</strong> — cuánto sale del
            banco cada mes.
          </Typography>
          <TableContainer
            sx={
              mobileLandscape
                ? { overflow: "auto", WebkitOverflowScrolling: "touch" }
                : undefined
            }
          >
            <Table size="small" stickyHeader={mobileLandscape}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>
                    Concepto
                  </TableCell>
                  {MESES_LABEL.map((label) => (
                    <TableCell
                      key={label}
                      align="right"
                      sx={{ fontWeight: 700 }}
                    >
                      {label}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tesoreria.filas.map((fila) => (
                  <TableRow key={fila.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {fila.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fila.cuenta} — {fila.nota}
                      </Typography>
                    </TableCell>
                    {MESES_LABEL.map((_, idx) => {
                      const mes = idx + 1;
                      const v = fila.meses[String(mes)] || 0;
                      return (
                        <TableCell
                          key={mes}
                          align="right"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {v ? formatearImporte(v) : "—"}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatearImporte(fila.total)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    "& .MuiTableCell-root": { fontWeight: 800 },
                  }}
                >
                  <TableCell>{tesoreria.total.label}</TableCell>
                  {MESES_LABEL.map((_, idx) => {
                    const mes = idx + 1;
                    const v = tesoreria.total.meses[String(mes)] || 0;
                    return (
                      <TableCell key={mes} align="right">
                        {v ? formatearImporte(v) : "—"}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    {formatearImporte(tesoreria.total.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}
    </Box>
  );
};

export default Nominas;
